import * as React from "react"
import { withRouter } from "react-router"
import queryString from "query-string"
import compose from "recompose/compose"
import styled, { css } from "styled-components"
import { withApollo } from "@apollo/react-hoc"
import { themeGet } from "@styled-system/theme-get"
import { Sidebar } from "@xingternal/winery-ui/lib/Sidebar"
import { routes } from "@/helpers"
import { canAccessYourPerformance, isAdmin } from "@/helpers/authorization"
import { withi18n } from "@/helpers/hoc/withi18n"
import { withFeatureFlags } from "@/helpers/hoc/withFeatureFlags"
import { withPermissions } from "@/helpers/hoc/withPermissions"
import { useMount } from "@/helpers/hooks"
import { OTMSidebarItems } from "./components/OTMSidebarItems"
import { SidebarHeaderLayout } from "./components/SidebarHeaderLayout"
import { RecruiterInsightsLicenseIcon } from "./components/RecruiterInsightsLicenseIcon"
import { Flex } from "@xingternal/winery-ui/lib/Flex"
import { Box } from "@xingternal/winery-ui/lib/Box"
import { Infolayer } from "@xingternal/winery-ui/lib/Infolayer"
import { RecruiterInsightsInfolayerContent } from "./components/RecruiterInsightsInfolayerContent"
import { RECRUITER_INSIGHTS_COLOR } from "./constants"
import { t } from "@/i18n"

const i18n = {
  yourPerformance: "360_RECRUITER_INSIGHTS_YOUR_PERFORMANCE",
  companyOverview: "360_RECRUITER_INSIGHTS_COMPANY_OVERVIEW",
  colleagues: "360_RECRUITER_INSIGHTS_COLLEAGUES",
  talentRadar: "360_RECRUITER_INSIGHTS_TALENT_RADAR",
  administration: "360_RECRUITER_INSIGHTS_ADMINISTRATION",
  collapse: "360_SIDEBAR_COLLAPSE",
}

const localStorage = window.localStorage || {}

const StyledSidebar = styled(Sidebar)`
  flex-shrink: 0;
  background: ${themeGet("colors.cardBackground")};
  box-shadow: 1px 0 0 #d1d1d1;
  position: relative;
  z-index: 4;
  h4 {
    margin: 0;
  }

  // Workaround to hide scrollbars when using Windows
  & > div {
    overflow: hidden;

    &:hover {
      overflow-y: scroll;
      overflow-x: hidden;
    }
  }
  & > div:nth-child(2) {
    overflow: hidden;
  }

  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      width: 220px;
    `}
`

const SidebarMenuWrapper = styled.div`
  margin-bottom: ${themeGet("space.3")};
`

const AnimatedIconContainer = styled(Box)`
  padding-left: 14px;
  animation: fadeIn 4s;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`

const sidebarCollapsed = () => {
  try {
    return localStorage.sidebarCollapsed === "true" || false
  } catch (e) {
    return false
  }
}

const NavigationSidebar = ({
  client,
  enabledFeatures,
  history,
  location,
  permissions,
  ...i18n
}) => {
  const [items, setItems] = React.useState([])
  const [activeItem, setActiveItem] = React.useState(location.pathname)
  const [collapsed, setCollapsed] = React.useState(sidebarCollapsed())

  const buildNavigationItem = ({
    icon,
    label,
    isNewItem = false,
    path,
    PropActionOrigin,
  }) => {
    return {
      active: activeItem === path,
      id: path,
      icon,
      label,
      isNewItem,
      onClick: () => {
        const searchQuery = queryString.parse(window.location.search)
        searchQuery.sc_o = "xri_navigation"
        searchQuery.sc_o_PropActionOrigin = PropActionOrigin
        const search = queryString.stringify(searchQuery)

        setActiveItem({ activeItem: path })

        history.push(`${path}?${search}`)
      },
    }
  }

  const buildNavigation = () => {
    const navigationItems = []

    canAccessYourPerformance({ enabledFeatures, permissions }) &&
      navigationItems.push(
        buildNavigationItem({
          path: routes.yourPerformance,
          icon: "CoreGraphHigh",
          label: i18n.yourPerformance,
          PropActionOrigin: "navi_your_performance",
        }),
      )

    navigationItems.push(
      buildNavigationItem({
        path: routes.companyOverview,
        icon: "NavigationCompanies",
        label: i18n.companyOverview,
        PropActionOrigin: "navi_team_overview",
      }),
    )

    isAdmin(permissions) &&
      navigationItems.push(
        buildNavigationItem({
          path: routes.colleagues,
          icon: "CoreContacts",
          label: i18n.colleagues,
          PropActionOrigin: "navi_colleagues",
        }),
      )

    navigationItems.push(
      buildNavigationItem({
        path: routes.talentRadar,
        icon: "TalentRadar",
        label: i18n.talentRadar,
        PropActionOrigin: "navi_talent_radar",
      }),
    )

    navigationItems.push(
      buildNavigationItem({
        path: routes.administration,
        icon: "CoreSettings",
        label: i18n.administration,
        PropActionOrigin: "navi_administration",
      }),
    )

    setItems(navigationItems)
  }

  const onCollapse = () => {
    setCollapsed(true)
    localStorage.sidebarCollapsed = true
  }

  const onExpand = () => {
    setCollapsed(false)
    localStorage.sidebarCollapsed = false
  }

  useMount(buildNavigation)

  return (
    <>
      <StyledSidebar
        onCollapse={onCollapse}
        onExpand={onExpand}
        isCollapsed={collapsed}
        data-testid="navigation-sidebar"
        $isExpanded={!collapsed}
        collapseLabel={t(i18n.collapse)}
        pb={4}
      >
        <OTMSidebarItems isCollapsed={collapsed} permissions={permissions} />
        <Flex alignItems="center" mt={3}>
          <SidebarHeaderLayout
            backgroundColor={RECRUITER_INSIGHTS_COLOR}
            icon="Insights"
            isCollapsed={collapsed}
          >
            Recruiter Insights
          </SidebarHeaderLayout>
          {!collapsed && (
            <Infolayer
              position="right"
              content={<RecruiterInsightsInfolayerContent />}
              padding={5}
              closeDelay={1}
            >
              <AnimatedIconContainer>
                <RecruiterInsightsLicenseIcon />
              </AnimatedIconContainer>
            </Infolayer>
          )}
        </Flex>
        <SidebarMenuWrapper data-testid="sidebar-menu-wrapper">
          {items.map(({ active, icon, isNewItem, label, onClick }, i) => (
            <Sidebar.Item
              isActive={active}
              icon={icon}
              onClick={onClick}
              isNewItem={isNewItem}
              key={i}
            >
              {label}
            </Sidebar.Item>
          ))}
        </SidebarMenuWrapper>
      </StyledSidebar>
    </>
  )
}

const enhance = compose(
  withi18n(i18n),
  withRouter,
  withApollo,
  withFeatureFlags,
  withPermissions,
)

export default enhance(NavigationSidebar)
